import { Color } from "@repo/app-types/constants/colors";
import { Size } from "@repo/ui/helpers/helpers";
import { Pill } from "@repo/ui/Pills/pill";
import type { FC, ReactNode } from "react";
import { ListBoxItem } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { tv } from "tailwind-variants";

export type SearchModalRowDensity = "regular" | "compact";

/**
 * Row geometry, shared with the loading skeleton (`searchModalLoadingRow`) so
 * a placeholder occupies exactly the same box as the row that replaces it and
 * results land without shifting the list.
 */
export const searchModalRowStyle = tv({
  base: "-mx-2 flex w-[calc(100%+1rem)] min-w-0 items-center gap-3 rounded-lg pr-4 pl-2 text-left",
  variants: {
    density: {
      regular: "py-2.5",
      compact: "h-10",
    },
  },
  defaultVariants: {
    density: "regular",
  },
});

/** Icon slot; fixed width so the text column starts at the same offset. */
export const searchModalRowIconClassName =
  "flex size-6 shrink-0 items-center justify-center";

/** The two text lines — a regular row is as tall as their line boxes. */
export const searchModalRowTitleClassName = "text-sm leading-5";
export const searchModalRowSubtitleClassName = "text-xs leading-4";

/**
 * Stand-ins for those lines in the skeleton: a placeholder bar has no line
 * box, so the heights are pinned here and must track the `leading-*` above.
 */
export const searchModalRowTitleLineClassName = "flex h-5 items-center";
export const searchModalRowSubtitleLineClassName = "flex h-4 items-center";

export interface SearchModalRowProps {
  id: string;
  title: string;
  subtitle?: string;
  badge?: string;
  trailing?: ReactNode;
  icon?: ReactNode;
  density?: SearchModalRowDensity;
  className?: string;
  onAction: () => void;
}

/**
 * A single actionable row in the search modal. Rendered as a `ListBoxItem` so
 * the surrounding `Autocomplete`/`ListBox` provide arrow-key navigation and
 * Enter-to-select from the search field (`data-focused` marks the virtually
 * focused row).
 */
export const SearchModalRow: FC<SearchModalRowProps> = (props) => {
  const {
    id,
    title,
    subtitle,
    badge,
    trailing,
    icon,
    density = "regular",
    className,
    onAction,
  } = props;

  return (
    <ListBoxItem
      id={id}
      textValue={title}
      onAction={onAction}
      className={searchModalRowStyle({
        density,
        className: twMerge(
          "group cursor-pointer transition-colors outline-none",
          "data-[focused]:bg-muted data-[hovered]:bg-muted",
          className,
        ),
      })}
    >
      {icon ? (
        <span
          className={twMerge(searchModalRowIconClassName, "text-foreground")}
        >
          {icon}
        </span>
      ) : null}
      <span className="min-w-0 flex-1">
        <span
          className={twMerge(
            "block truncate font-medium text-foreground",
            searchModalRowTitleClassName,
          )}
        >
          {title}
        </span>
        {subtitle ? (
          <span
            className={twMerge(
              "block truncate text-muted-foreground",
              searchModalRowSubtitleClassName,
            )}
          >
            {subtitle}
          </span>
        ) : null}
      </span>
      {badge ? (
        <Pill
          className="shrink-0"
          color={Color.Gray}
          label={badge}
          size={Size.SMALL}
        />
      ) : null}
      {trailing ? (
        <span className="shrink-0 text-xs text-muted-foreground">
          {trailing}
        </span>
      ) : null}
    </ListBoxItem>
  );
};
